/**
 * TabChartRenderer - Multi-tab adapted version of ChartRenderer from e80c574d
 * 
 * Extends ChartRenderer with chart ID namespacing for multi-tab support
 * while maintaining the proven rendering architecture
 */

import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import { getLocalStorageItem } from '../../../../../utils/localStorageUtils';
import { EventStream } from '../core/EventStream';
import { extractBooleanFromEvent, isEventForChart } from '../helpers/ChakraEventHelper';
import { getFormattedPlanetDisplayName, getRashiDisplayName } from '../config/PlanetLayerConfig';
// DivisionalChakraHelper removed - migrated to UniversalChakraRenderer
import { DivisionalChakraType } from '../../../../../config/divisionalChakraConfig';
import { ElementPreparationEngine } from '../engines/ElementPreparationEngine';
import { ElementCalculationEngine, UnifiedElement } from '../engines/ElementCalculationEngine';
import { ElementRenderingEngine } from '../engines/ElementRenderingEngine';
import { ContainerBounds } from '../engines/ElementCalculationEngine';
import * as d3 from 'd3';
import { chartColorPresetManager } from '../../../../../config/chartColorPresets';
// MuhurtaChakraDivisionsRenderer removed - migrated to UniversalChakraRenderer
// AshtamshaChakraDivisionsRenderer removed - migrated to UniversalChakraRenderer
// NakshatraChakraDivisionsRenderer removed - migrated to UniversalChakraRenderer
// NakshatraPadaDivisionsRenderer removed - migrated to UniversalChakraRenderer
// DivisionalChakraRenderer removed - migrated to UniversalChakraRenderer
import { getDefaultMuhurtaChakraVisibility } from '../../../../../config/muhurtaDefaults';
import { getDefaultAshtamshaChakraVisibility } from '../../../../../config/ashtamshaDefaults';
import { getDefaultNakshatraChakraVisibility, getDefaultNakshatraPadaVisibility } from '../../../../../config/nakshatraDefaults';
import { ReadyChartData } from '../../../../../types/ReadyChartData';
import { chakraDataService } from '../../../../../services/ChakraDataService';
import { DisplayModeService } from '../../services/DisplayModeService';
import type { ChartViewMode } from '../../../../../types/ChartViewTypes';
import { ChartViewManager } from './ChartViewManager';
import { HoraClockView, RashiChakraView } from './views';

// Sanskrit Rashi names moved to PlanetLayerConfig.ts - use getRashiDisplayName()

// Removed local createAstronomicalData function - now using unified validation from utils

// Legacy ChartData interface removed - only ReadyChartData supported

export interface ChartRenderOptions {
    displayMode: 'symbols' | 'short';
    enableAnimations: boolean;
    debugMode: boolean;
    showMuhurtaSectors: boolean; // Show/hide muhurta sectors layer
    showAshtamshaSectors: boolean; // Show/hide D8 Ashtamsha sectors layer
    showNakshatraSectors: boolean; // Show/hide Nakshatra sectors layer
    showNakshatraPada: boolean; // Show/hide Nakshatra Pada divisions layer (depends on showNakshatraSectors)

    // Universal divisional chakras
    showD7Sectors: boolean;
    showD8Sectors: boolean;  // Alternative to showAshtamshaSectors
    showD12Sectors: boolean;
    showD20Sectors: boolean;
    showD24Sectors: boolean;
    showD30Sectors: boolean;
    showD40Sectors: boolean;
    showD60Sectors: boolean;
}

// ChartViewMode and SPECIAL_VIEW_GROUPS imported from types/ChartViewTypes

/**
 * Tab-aware chart renderer with chart ID namespace isolation
 */
export class TabChartRenderer {
    private eventStream: EventStream;
    private containers: Map<string, ContainerBounds> = new Map();
    private isInitialized: boolean = false;
    private chartId: string;
    private eventSubscriptions: Array<{ unsubscribe: () => void }> = [];

    // Engines - shared instances for performance
    private engines: {
        preparation: ElementPreparationEngine;
        calculation: ElementCalculationEngine;
        rendering: ElementRenderingEngine;
    };

    // View switching manager (hora clock, rashi chakra, etc.)
    private viewManager: ChartViewManager;

    // Pending muhurta data for delayed rendering
    private pendingChartData: ReadyChartData | null = null;

    // Lazy planet rendering state
    private pendingPlanetRendering: boolean = false;

    // Current chart data for re-rendering when needed
    private currentChartData: ReadyChartData | null = null;

    // Backup storage for sunrise longitude to prevent data loss
    private pendingSunriseLongitude: string | null = null;
    
    // Current options
    private currentOptions: ChartRenderOptions = {
        displayMode: 'short',
        enableAnimations: false,
        debugMode: false, // ✅ ВЫКЛЮЧЕНО для продакшена
        showMuhurtaSectors: getDefaultMuhurtaChakraVisibility(), // ✅ Централизованный дефолт
        showAshtamshaSectors: getDefaultAshtamshaChakraVisibility(), // ✅ Централизованный дефолт D8
        showNakshatraSectors: getDefaultNakshatraChakraVisibility(), // ✅ Централизованный дефолт Nakshatra
        showNakshatraPada: getDefaultNakshatraPadaVisibility(), // ✅ Дефолт для Pada divisions (зависит от Nakshatra)

        // Universal divisional chakras defaults
        showD7Sectors: false,
        showD8Sectors: false,
        showD12Sectors: false,
        showD20Sectors: false,
        showD24Sectors: false,
        showD30Sectors: false,
        showD40Sectors: false,
        showD60Sectors: false
    };

    // НОВАЯ СИСТЕМА: Статус всех рендеров для оптимизации
    private renderStatus = {
        tab: {
            isActive: false,             // Активен ли таб
            isDOMReady: false,           // Готова ли DOM структура
            isPlanetsReady: false,       // Готовы ли планеты (плейсхолдеры созданы)
            readyTime: 0                 // Время готовности
        },
        planets: {
            isRendered: false,           // Были ли отрендерены планеты
            isVisible: true,             // Видимы ли планеты
            renderTime: 0,               // Время последнего рендера
            elementCount: 0              // Количество элементов
        },
        muhurta: {
            isRendered: false,           // Была ли отрендерена мухурта
            isVisible: false,            // Видима ли мухурта
            renderTime: 0,               // Время последнего рендера
            divisionCount: 0             // Количество делений
        },
        ashtamsha: {
            isRendered: false,           // Была ли отрендерена аштамша
            isVisible: false,            // Видима ли аштамша
            renderTime: 0,               // Время последнего рендера
            divisionCount: 0             // Количество делений (8 для D8)
        },
        nakshatra: {
            isRendered: false,           // Была ли отрендерена накшатра
            isVisible: false,            // Видима ли накшатра
            renderTime: 0,               // Время последнего рендера
            divisionCount: 0,            // Количество накшатр (27)
            // Pada handled by UniversalChakraRenderer subdivisions
            isPadaRendered: false,       // Была ли отрендерена пада
            isPadaVisible: false         // Видима ли пада
        },
        houseNames: {
            isVisible: false             // Видимы ли имена домов
        },
        // horaClock and rashiChakra: tracked by ChartViewManager view adapters
    };
    
    constructor(eventStream: EventStream, debugMode: boolean = false, chartId: string) {
        this.eventStream = eventStream;
        this.currentOptions.debugMode = debugMode;
        this.chartId = chartId;
        
        // Initialize engines with chart ID context
        this.engines = {
            preparation: ElementPreparationEngine.getInstance(),
            calculation: ElementCalculationEngine.getInstance(),
            rendering: ElementRenderingEngine.getInstance()
        };
        
        // Configure rendering engine for this chart instance
        this.configureRenderingEngineForChart();
        
        this.setupEventSubscriptions();

        // View switching manager
        this.viewManager = new ChartViewManager(chartId, this.eventStream);
        this.viewManager.registerView(new HoraClockView(chartId));
        this.viewManager.registerView(new RashiChakraView(chartId));
        this.viewManager.setChakraRestoreCallback(() => this.onChakraViewRestored());

        // Register this renderer globally for lazy planet rendering
        if (!window.tabChartRenderers) {
            window.tabChartRenderers = new Map();
        }
        window.tabChartRenderers.set(chartId, this);

        if (debugMode) {
        }
    }
    
    /**
     * Configure rendering engine for chart-specific selectors
     * DISABLED: Global method override causes issues with multiple charts
     */
    private configureRenderingEngineForChart(): void {
        // DISABLED: Overriding singleton methods breaks multi-chart support
        // Each chart now passes chart-specific parameters directly to renderElements
        if (this.currentOptions.debugMode) {
        }
    }
    
    /**
     * Main rendering method with chart ID namespace - only ReadyChartData supported
     */
    async renderChart(chartData: ReadyChartData, options?: Partial<ChartRenderOptions>): Promise<{
        success: boolean;
        elementsRendered: number;
        renderTime: number;
    }> {
        const startTime = performance.now();

        // Debug: renderChart called (production: logs disabled)

        // Store current chart data for potential re-rendering (muhurta, etc.)
        this.currentChartData = chartData;

        // Update options
        if (options) {
            this.currentOptions = { ...this.currentOptions, ...options };
        }

        // Sync muhurta visibility from chart settings (like planets)
        try {
            const multipleChartsContext = window.multipleChartsContext;
            if (multipleChartsContext && this.chartId) {
                const activeChart = multipleChartsContext.getChart?.(this.chartId);
                if (activeChart?.settings?.showMuhurtaChakra !== undefined) {
                    this.currentOptions.showMuhurtaSectors = activeChart.settings.showMuhurtaChakra;
                }
                if (activeChart?.settings?.showAshtamshaChakra !== undefined) {
                    this.currentOptions.showAshtamshaSectors = activeChart.settings.showAshtamshaChakra;
                }
                if (activeChart?.settings?.showNakshatraChakra !== undefined) {
                    this.currentOptions.showNakshatraSectors = activeChart.settings.showNakshatraChakra;
                }
            }
        } catch (error) {
            devWarn(`⚠️ TabChartRenderer[${this.chartId}]: Failed to sync muhurta settings:`, error);
        }
        
        // Ensure initialization before rendering
        if (!this.isInitialized) {
            if (this.currentOptions.debugMode) {
            }
            this.initializeDOMStructure();
            
            // If still not initialized after DOM structure init, we can't render
            if (!this.isInitialized) {
                devWarn(`⚠️ TabChartRenderer[${this.chartId}]: Cannot render - initialization failed`);
                return { success: false, elementsRendered: 0, renderTime: performance.now() - startTime };
            }
        }
        
        if (this.currentOptions.debugMode) {
            // if (chartData.hora) {
            // }
        }
        
        try {
            // Set chart ID FIRST - critical for correct sprite manager selection
            this.engines.rendering.setChartId(this.chartId);

            // Clear existing sprites before re-rendering (fixes duplicate planets on update)
            if (this.engines.rendering) {
                devLog(`[TabChartRenderer] 🧹 Clearing sprites before render for ${this.chartId}`);
                this.engines.rendering.clearSpritesForRerender();
            }

            // Extract data from ReadyChartData
            const planets = chartData.planets;
            const lagnaPosition = chartData.lagna;
            const sunriseLongitude = chartData.astronomical.sunrise_surya_longitude;

            if (!planets || planets.length === 0) {
                devError(`❌ TabChartRenderer[${this.chartId}]: No planets data provided`);
                throw new Error('No planets data provided');
            }

            if (!lagnaPosition) {
                devError(`❌ TabChartRenderer[${this.chartId}]: No lagna data provided`);
                throw new Error('No lagna data provided');
            }

            if (!sunriseLongitude) {
                devError(`❌ TabChartRenderer[${this.chartId}]: Missing required sunrise_surya_longitude`);
                throw new Error('Missing required astronomical data: sunrise_surya_longitude');
            }
            
            if (this.currentOptions.debugMode) {
            }
            
            // Debug: HORA preparation (production: logs disabled)

            // Extract hora current_planet from ReadyChartData
            const horaCurrentPlanet = chartData.hora?.currentPlanet;
            //

            // 1. Create UnifiedElement from raw API data directly
            const elements = (chartData.rawApiPlanets || [])
                .map(apiPlanet => {
                    // Find corresponding processed planet data
                    const processedPlanet = chartData.planets.find(p => p.graha.toString() === apiPlanet.short_name) ||
                                           (apiPlanet.short_name === 'Lg' ? chartData.lagna : null);

                    if (!processedPlanet?.position) return null;

                    // Use correct Vedic mapping: 0° = Mesha (containerIndex=1), 330° = Meena (containerIndex=0)
                    const rasiIndex = Math.floor(apiPlanet.longitude / 30) % 12;
                    const containerIndex = rasiIndex === 11 ? 0 : rasiIndex + 1;
                    const houseNumber = rasiIndex + 1;  // House numbers are 1-12
                    const degree = apiPlanet.longitude % 30;

                    return {
                        id: `planet-${apiPlanet.short_name}`,
                        type: 'planet' as const,
                        name: apiPlanet.name,       // Full API name: "Surya", "Lagna", etc.
                        displayName: apiPlanet.short_name,  // Short name for display
                        position: processedPlanet,  // Processed planet data for calculations
                        containerIndex,
                        houseNumber,
                        degree,
                        special: 'none' as const   // Will be updated later for hora
                    };
                })
                .filter(element => element !== null) as UnifiedElement[];
            
            if (this.currentOptions.debugMode) {
            }
            
            // 2. Calculate positions
            const containerBounds = Array.from(this.containers.values());
            if (containerBounds.length === 0) {
                devWarn(`⚠️ TabChartRenderer[${this.chartId}]: No container bounds available, skipping render`);
                return { success: false, elementsRendered: 0, renderTime: 0 };
            }
            
            const positioned = this.engines.calculation.calculateElementPositions(
                elements,
                containerBounds,
                this.currentOptions.displayMode
            );
            
            if (this.currentOptions.debugMode) {
            }
            
            // 3. Render with chart ID namespace
            const targetLayer = this.getTargetLayer();
            if (!targetLayer) {
                devWarn(`⚠️ TabChartRenderer[${this.chartId}]: Target layer not found, skipping render`);
                return { success: false, elementsRendered: 0, renderTime: 0 };
            }

            // Debug: About to render elements (production: logs disabled)

            // Get chart-specific lines overlay for degree lines
            const chartSpecificLinesOverlay = this.ensureLinesOverlay();

            if (this.currentOptions.debugMode && chartSpecificLinesOverlay) {
            }

            // Debug: Calling renderElements (production: logs disabled)

            // LAZY PLANET RENDERING: Only render planets for active/visible tabs
            const isChartVisible = this.isChartVisible();
            // Debug: Chart visibility check (production: logs disabled)

            if (isChartVisible) {
                // CRITICAL: Call ElementPreparationEngine to prepare planet sprites BEFORE rendering
                // Debug: Chart is VISIBLE (production: logs disabled)

                try {
                    const preparationEngine = (await import('../engines/ElementPreparationEngine')).ElementPreparationEngine.getInstance();
                    const preparationInput = {
                        planets: chartData.planets,
                        lagna: chartData.lagna,
                        hora: chartData.hora,
                        astronomical: chartData.astronomical
                    };

                    const preparationResult = preparationEngine.prepareElements(preparationInput, {
                        debugMode: false,
                        enableYogaGrouping: false,
                        yogaGroupingMode: 0,
                        degreeTolerance: 5,
                        includeRetrogradePlanets: true
                    });

                } catch (error) {
                    devError(`❌ TabChartRenderer[${this.chartId}]: Failed to prepare elements:`, error);
                }
            } else {
                // Store chart data for lazy rendering when tab becomes visible
                this.pendingChartData = chartData;
                this.pendingPlanetRendering = true;
            }

            const renderResult = this.engines.rendering.renderElements(
                positioned,
                targetLayer,
                chartSpecificLinesOverlay || undefined,
                undefined,
                {
                    chartId: this.chartId,  // Add chart ID for proper isolation
                    mode: this.currentOptions.displayMode,
                    showDegreelines: true,
                    showYogaGroups: false,
                    showPlanets: isChartVisible, // Only show planets for visible charts
                    horaCurrentPlanet: horaCurrentPlanet,  // Pass hora current planet for direct API-based detection
                    enableAnimations: this.currentOptions.enableAnimations,
                    debugMode: this.currentOptions.debugMode
                },
                chartData.eventSource
            );
            
            const renderTime = performance.now() - startTime;
            
            // Muhurta now uses UniversalChakraRenderer via event system
            // Store data for potential later use
            this.pendingChartData = chartData;
            this.pendingSunriseLongitude = sunriseLongitude;
            
            if (this.currentOptions.debugMode) {
            }
            
            // НОВАЯ СИСТЕМА: Обновить статус планет после успешного рендера
            this.renderStatus.planets.isRendered = true;
            this.renderStatus.planets.isVisible = true;
            this.renderStatus.planets.renderTime = performance.now();
            this.renderStatus.planets.elementCount = renderResult.totalElements;

            // Установить флаги готовности DOM и планет
            this.renderStatus.tab.isDOMReady = true;
            this.renderStatus.tab.isPlanetsReady = true;

            // Debug: Planet render status (production: logs disabled)
            // Debug: Tab readiness updated (production: logs disabled)

            // Проверить готовность для инициализации чакр (только если таб активен)
            this.checkChakraInitializationReadiness();

            // Restore divisional chakras from saved settings (after currentChartData is set)
            this.restoreDivisionalChakrasState();

            // Emit DOM ready event for event-driven components
            this.eventStream.emit({
                id: `chart-dom-ready-${this.chartId}-${Date.now()}`,
                type: 'chart.dom.ready',
                source: `tab-chart-renderer-${this.chartId}`,
                chartId: this.chartId,
                timestamp: Date.now(),
                metadata: {
                    elementsRendered: renderResult.totalElements,
                    renderTime: performance.now()
                }
            });
            // Debug: Emitted chart.dom.ready event (production: logs disabled)

            return {
                success: true,
                elementsRendered: renderResult.totalElements,
                renderTime
            };
            
        } catch (error) {
            devError(`❌ TabChartRenderer[${this.chartId}]: Render failed:`, error);
            return { success: false, elementsRendered: 0, renderTime: performance.now() - startTime };
        }
    }
    // renderPendingMuhurtaDivisions removed - muhurta migrated to UniversalChakraRenderer
    
    /**
     * Switch display mode for this chart
     */
    async switchDisplayMode(mode: 'symbols' | 'short'): Promise<void> {
        if (this.currentOptions.displayMode === mode) return;
        
        this.currentOptions.displayMode = mode;
        
        if (this.currentOptions.debugMode) {
        }
    }
    
    /**
     * Update container bounds
     */
    updateContainers(containers: Map<string, ContainerBounds>): void {
        this.containers = new Map(containers);
        this.isInitialized = this.containers.size > 0;
        
        if (this.currentOptions.debugMode) {
        }
    }
    
    // DOM initialization state tracking
    private domInitialized = false;
    private containersReadyEmitted = false;
    
    /**
     * Initialize DOM structure - using chart ID indexed selectors
     */
    private initializeDOMStructure(): void {
        // Look for chart-specific viewport using indexed selector
        const viewportContent = d3.select(`.viewport-content-${this.chartId}`);
        
        // Also check what viewports exist in DOM for debugging
        // const allViewports = document.querySelectorAll('[class*="viewport-content"]');
        
        if (viewportContent.empty()) {
            if (this.currentOptions.debugMode) {
                devWarn(`⚠️ TabChartRenderer[${this.chartId}]: Viewport .viewport-content-${this.chartId} not found, retrying in 100ms...`);
            }
            
            // Use event-driven approach: schedule initialization retry
            this.scheduleEventBasedInitialization();
            return;
        }
        
        if (this.currentOptions.debugMode) {
        }
        
        // Create standard containers with chart ID namespace (event-driven)
        this.createStandardContainersEventBased(viewportContent);
        this.domInitialized = true;
        
        // Create background elements
        this.createBackgroundElements(viewportContent);
        
        // Generate container bounds
        this.generateContainerBounds();
        
        // Emit containers ready event
        this.emitContainersReady();
    }
    
    /**
     * Schedule event-based initialization retry
     */
    private scheduleEventBasedInitialization(): void {
        // Subscribe to DOM ready events if not already subscribed
        if (!this.domInitialized) {
            const domReadyHandler = () => {
                const viewportContent = d3.select(`.viewport-content-${this.chartId}`);
                if (!viewportContent.empty()) {
                    if (this.currentOptions.debugMode) {
                    }
                    this.createStandardContainersEventBased(viewportContent);
                    this.createBackgroundElements(viewportContent);
                    this.generateContainerBounds();
                    this.emitContainersReady();
                    this.domInitialized = true;
                }
            };
            
            // Retry with timeout as fallback
            setTimeout(() => {
                if (!this.domInitialized) {
                    domReadyHandler();
                }
            }, 100);
        }
    }
    
    /**
     * Create standard containers - event-driven approach
     */
    private createStandardContainersEventBased(chartContent: any): void {
        // Prevent duplicate container creation
        if (this.containersReadyEmitted) {
            if (this.currentOptions.debugMode) {
            }
            return;
        }
        
        this.createStandardContainers(chartContent);
        this.containersReadyEmitted = true;
    }
    
    /**
     * Create standard containers with chart ID indexes
     */
    private createStandardContainers(chartContent: any): void {
        // Only log in debug mode and only once per chart
        if (this.currentOptions.debugMode && !this.containersReadyEmitted) {
        }
        
        // Use standard container names (isolation provided by viewport-content-chartId)
        // Z-order: background → house-names → nakshatra-sectors → muhurta-sectors → ashtamsha-sectors → divisional chakras → lines-overlay
        const containers = [
            { class: 'background-group', id: `background-container-${this.chartId}` },
            { class: 'house-names-group', id: `house-names-container-${this.chartId}` },
            { class: 'nakshatra-sectors-group', id: `nakshatra-sectors-container-${this.chartId}` },
            { class: 'muhurta-sectors-group', id: `muhurta-sectors-container-${this.chartId}` },
            { class: 'ashtamsha-sectors-group', id: `ashtamsha-sectors-container-${this.chartId}` },
            // Universal divisional chakras
            { class: 'd7-sectors-group', id: `d7-sectors-container-${this.chartId}` },
            { class: 'd8-sectors-group', id: `d8-sectors-container-${this.chartId}` },
            { class: 'd12-sectors-group', id: `d12-sectors-container-${this.chartId}` },
            { class: 'd20-sectors-group', id: `d20-sectors-container-${this.chartId}` },
            { class: 'd24-sectors-group', id: `d24-sectors-container-${this.chartId}` },
            { class: 'd30-sectors-group', id: `d30-sectors-container-${this.chartId}` },
            { class: 'd40-sectors-group', id: `d40-sectors-container-${this.chartId}` },
            { class: 'd60-sectors-group', id: `d60-sectors-container-${this.chartId}` },
            { class: 'lines-overlay', id: `lines-overlay-${this.chartId}` },
            // Hora Clock — separate view, hidden by default
            { class: 'hora-clock-group', id: `hora-clock-container-${this.chartId}` },
            // Rashi Chakra — separate view, hidden by default
            { class: 'rashi-chakra-group', id: `rashi-chakra-container-${this.chartId}` }
        ];
        
        // Force correct z-order by removing and recreating all layers in proper sequence
        containers.forEach(container => {
            // Remove existing layer if present
            (chartContent as any).select(`.${container.class}`).remove();
        });

        // Create layers in correct z-order
        containers.forEach(container => {
            const group = (chartContent as any).append('g')
                .attr('class', container.class)
                .attr('id', container.id);

            // Create nakshatra placeholder immediately in the correct z-order position
            if (container.class === 'nakshatra-sectors-group') {
                group.append('g')
                    .attr('class', 'nakshatra-divisions-layer')
                    .attr('id', `nakshatra-divisions-${this.chartId}`)
                    .style('display', 'none'); // Hidden by default, will be shown when populated

                if (this.currentOptions.debugMode) {
                }
            }

            // Create muhurta placeholder immediately in the correct z-order position
            if (container.class === 'muhurta-sectors-group') {
                group.append('g')
                    .attr('class', 'muhurta-divisions-layer')
                    .attr('id', `muhurta-divisions-${this.chartId}`)
                    .style('display', 'none'); // Hidden by default, will be shown when populated

                if (this.currentOptions.debugMode) {
                }
            }

            // Create ashtamsha placeholder immediately in the correct z-order position
            if (container.class === 'ashtamsha-sectors-group') {
                group.append('g')
                    .attr('class', 'ashtamsha-divisions-layer')
                    .attr('id', `ashtamsha-divisions-${this.chartId}`)
                    .style('display', 'none'); // Hidden by default, will be shown when populated

                if (this.currentOptions.debugMode) {
                }
            }

            // Create universal divisional chakra placeholders
            const divisionalTypes = ['d7', 'd8', 'd12', 'd20', 'd24', 'd30', 'd40', 'd60'];
            divisionalTypes.forEach(divType => {
                if (container.class === `${divType}-sectors-group`) {
                    group.append('g')
                        .attr('class', `${divType}-divisions-layer`)
                        .attr('id', `${divType}-divisions-${this.chartId}`)
                        .style('display', 'none'); // Hidden by default

                    if (this.currentOptions.debugMode) {
                    }
                }
            });

            // Hora Clock container — hidden by default
            if (container.class === 'hora-clock-group') {
                group.style('display', 'none');
            }

            // Rashi Chakra container — hidden by default
            if (container.class === 'rashi-chakra-group') {
                group.style('display', 'none');
            }

            if (this.currentOptions.debugMode) {
            }
        });

        // MuhurtaChakraDivisionsRenderer will be initialized immediately in emitContainersReady
    }
    
    /**
     * Create background elements (12 houses) with chart ID index
     */
    private createBackgroundElements(chartContent: any): void {
        const backgroundGroup = (chartContent as any).select('.background-group');

        if (backgroundGroup.empty()) {
            if (this.currentOptions.debugMode) {
                devWarn(`⚠️ TabChartRenderer[${this.chartId}]: Background group not found`);
            }
            return;
        }

        // MODERN ARCHITECTURE: Create houses only once (no clearing needed for multiple charts)
        const existingHouses = backgroundGroup.selectAll('.house-background').size();
        if (existingHouses === 12) {
            if (this.currentOptions.debugMode) {
            }
            return;
        }

        // Debug: Creating missing background houses (production: logs disabled)
        
        // Create 12 background rectangles for houses
        for (let house = 0; house < 12; house++) {
            const position = this.calculateHousePosition(house);

            backgroundGroup.append('rect')
                .attr('class', `house-background house-${house} chart-${this.chartId}`)
                .attr('data-house', house)
                .attr('data-chart-id', this.chartId)
                .attr('x', position.x)
                .attr('y', position.y)
                .attr('width', position.width)
                .attr('height', position.height)
                .attr('fill', chartColorPresetManager.getCurrentPreset().detailsPanel.background)
                .attr('stroke', chartColorPresetManager.getCurrentPreset().detailsPanel.borderDefault)
                .attr('stroke-width', 1)
                .attr('opacity', 1);
        }

        // Create chart-center container for logo and overlay content
        this.createChartCenterContainer(backgroundGroup);

        if (this.currentOptions.debugMode) {
        }
    }

    /**
     * Create chart-center container for centralized content (logo, overlays, etc.)
     * This container is positioned in the center of the chart (2x2 houses area)
     */
    private createChartCenterContainer(backgroundGroup: any): void {
        // Center square dimensions (2x2 houses)
        const cellSize = 690 / 4; // 172.5px
        const centerSquareSize = cellSize * 2; // 345px
        const centerSquareX = 177.5; // offset + cellSize
        const centerSquareY = 177.5;

        // Check if chart-center already exists
        let chartCenter = backgroundGroup.select(`.chart-center.chart-${this.chartId}`);
        if (!chartCenter.empty()) {
            return; // Already created
        }

        // Create chart-center group
        chartCenter = backgroundGroup.append('g')
            .attr('class', `chart-center chart-${this.chartId}`)
            .attr('data-chart-id', this.chartId);

        // Create logo container (bottom layer)
        chartCenter.append('g')
            .attr('class', `chart-center-logo chart-${this.chartId}`);

        // Create overlay container with foreignObject for HTML content (top layer)
        chartCenter.append('foreignObject')
            .attr('class', `chart-center-overlay chart-${this.chartId}`)
            .attr('x', centerSquareX)
            .attr('y', centerSquareY)
            .attr('width', centerSquareSize)
            .attr('height', centerSquareSize)
            .attr('pointer-events', 'none') // Allow clicks to pass through when empty
            .append('xhtml:div')
            .attr('class', 'chart-center-overlay-content')
            .attr('id', `chart-center-overlay-${this.chartId}`)
            .style('width', '100%')
            .style('height', '100%')
            .style('display', 'flex')
            .style('align-items', 'center')
            .style('justify-content', 'center')
            .style('pointer-events', 'none'); // Container doesn't capture events

        // Render logo if enabled
        this.renderCenterLogo(chartCenter.select(`.chart-center-logo.chart-${this.chartId}`));
    }

    /**
     * Render center logo (configurable image in center square)
     * Called after chart-center container is created
     */
    private renderCenterLogo(logoContainer: any): void {
        // Check if logo should be shown
        const showLogo = typeof window !== 'undefined' && window.globalShowLogo;

        if (!showLogo) {
            return;
        }

        // Get image config from global setting (force 'aum' for guest mode)
        const isGuestMode = typeof window !== 'undefined' && window.sharedViewMode === 'guest';
        const centerImageId = isGuestMode ? 'aum' : ((typeof window !== 'undefined' && window.globalCenterImage) || 'bhrigu');
        const imageConfig = this.getCenterImageConfig(centerImageId);

        // Center square dimensions (2x2 houses)
        const cellSize = 690 / 4; // 172.5px
        const centerSquareSize = cellSize * 2; // 345px
        const centerSquareY = 177.5;

        // Image dimensions based on config
        const imageHeight = centerSquareSize * imageConfig.scale;
        const imageWidth = imageHeight * imageConfig.aspectRatio;

        // Position: center horizontally, vertical based on config
        const imageX = 350 - (imageWidth / 2);
        const imageY = imageConfig.centerVertically
            ? centerSquareY + (centerSquareSize - imageHeight) / 2
            : centerSquareY;

        // Add image to logo container
        logoContainer.append('image')
            .attr('class', `center-logo chart-${this.chartId}`)
            .attr('x', imageX)
            .attr('y', imageY)
            .attr('width', imageWidth)
            .attr('height', imageHeight)
            .attr('href', imageConfig.path)
            .attr('opacity', 0.9)
            .attr('preserveAspectRatio', imageConfig.centerVertically ? 'xMidYMid meet' : 'xMidYMin meet');

        if (this.currentOptions.debugMode) {
        }
    }

    /**
     * Get center image configuration by ID
     */
    private getCenterImageConfig(imageId: string): { path: string; aspectRatio: number; scale: number; centerVertically: boolean } {
        const configs: Record<string, { path: string; aspectRatio: number; scale: number; centerVertically: boolean }> = {
            'aum': {
                path: '/img/aum-small.png',
                aspectRatio: 1, // 512x512 square
                scale: 0.4, // reduced 3x from 0.8
                centerVertically: true
            },
            'bhrigu': {
                path: '/img/Bhrigu Transparent Background.png',
                aspectRatio: 425 / 587, // ~0.724 portrait
                scale: 1, // full height
                centerVertically: false // align to top
            },
        };
        return configs[imageId] || configs['bhrigu'];
    }

    /**
     * Update logo visibility dynamically (called when settings change)
     */
    private updateLogoVisibility(showLogo: boolean): void {
        const svg = d3.select(`svg.chart-svg-${this.chartId}`);

        if (svg.empty()) {
            if (this.currentOptions.debugMode) {
                devWarn(`⚠️ TabChartRenderer[${this.chartId}]: SVG not found for logo update`);
            }
            return;
        }

        // Find logo container inside chart-center
        const logoContainer = svg.select(`.chart-center-logo.chart-${this.chartId}`);

        if (logoContainer.empty()) {
            if (this.currentOptions.debugMode) {
                devWarn(`⚠️ TabChartRenderer[${this.chartId}]: Logo container not found for logo update`);
            }
            return;
        }

        // Remove existing logo
        logoContainer.selectAll(`.center-logo.chart-${this.chartId}`).remove();

        // Add logo if showLogo is true
        if (showLogo) {
            this.renderCenterLogo(logoContainer);

            if (this.currentOptions.debugMode) {
            }
        } else {
            if (this.currentOptions.debugMode) {
            }
        }
    }

    /**
     * Calculate house position (same as original but with chart context)
     */
    private calculateHousePosition(house: number): { x: number, y: number, width: number, height: number } {
        const viewportSize = 700;
        const chartSize = 690;
        const cellSize = chartSize / 4;
        const offsetX = (viewportSize - chartSize) / 2;
        const offsetY = (viewportSize - chartSize) / 2;
        
        const positions = [
            { x: 0, y: 0 },                    // 0: 12-й дом
            { x: cellSize, y: 0 },             // 1: 1-й дом (Lagna)
            { x: cellSize * 2, y: 0 },         // 2: 2-й дом
            { x: cellSize * 3, y: 0 },         // 3: 3-й дом
            { x: cellSize * 3, y: cellSize },  // 4: 4-й дом
            { x: cellSize * 3, y: cellSize * 2 }, // 5: 5-й дом
            { x: cellSize * 3, y: cellSize * 3 }, // 6: 6-й дом
            { x: cellSize * 2, y: cellSize * 3 }, // 7: 7-й дом
            { x: cellSize, y: cellSize * 3 },  // 8: 8-й дом
            { x: 0, y: cellSize * 3 },         // 9: 9-й дом
            { x: 0, y: cellSize * 2 },         // 10: 10-й дом
            { x: 0, y: cellSize }              // 11: 11-й дом
        ];
        
        const pos = positions[house] || { x: 0, y: 0 };
        
        return {
            x: pos.x + offsetX,
            y: pos.y + offsetY,
            width: cellSize,
            height: cellSize
        };
    }
    
    /* REMOVED: initializeMuhurtaDivisionsRenderer and initializeMuhurtaRendererWithGroup
     * Migration: Muhurta now uses UniversalChakraRenderer via event system
     * Date: 2025-01-21
     */

    /* REMOVED: initializeAshtamshaDivisionsRenderer and initializeAshtamshaRendererWithGroup
     * Migration: Ashtamsha now uses UniversalChakraRenderer via event system
     * Date: 2025-01-20
     * See: docs/migration/ashtamsha-to-universal-migration-plan.md
     */

    // REMOVED: Legacy Nakshatra and Pada initialization methods
    // Nakshatra and Pada are now handled by UniversalChakraRenderer
    // with subdivision support via event-driven architecture

    /**
     * Generate container bounds
     */
    private generateContainerBounds(): void {
        const containerBounds = new Map();
        
        const mainBounds = {
            width: 700,
            height: 700,
            x: 0,
            y: 0
        };
        
        const mainContainerIds = [
            `background-container-${this.chartId}`
        ];
        
        mainContainerIds.forEach(id => {
            containerBounds.set(id, mainBounds);
        });
        
        // Bounds for 12 houses
        for (let house = 0; house < 12; house++) {
            const houseBounds = this.calculateHousePosition(house);
            containerBounds.set(`${house}-${this.chartId}`, houseBounds);
        }
        
        this.updateContainers(containerBounds);
        
        if (this.currentOptions.debugMode) {
        }
    }
    
    /**
     * Emit containers ready event with chart ID
     */
    private emitContainersReady(): void {
        // Prevent duplicate emission
        if (this.containersReadyEmitted) {
            if (this.currentOptions.debugMode) {
            }
            return;
        }

        const containerBounds: { [key: string]: any } = {};

        this.containers.forEach((bounds, id) => {
            containerBounds[id] = bounds;
        });

        this.eventStream.emit({
            id: `containers-ready-${this.chartId}-${Date.now()}`,
            type: 'containers.ready',
            source: `tab-chart-renderer-${this.chartId}`,
            chartId: this.chartId,
            timestamp: Date.now(),
            metadata: {
                containerBounds,
                containerIds: Array.from(this.containers.keys()),
                chartId: this.chartId
            }
        });

        // Mark as emitted to prevent duplicates
        this.containersReadyEmitted = true;

        // MuhurtaChakraDivisionsRenderer will be initialized on-demand when needed (event-driven architecture)

        if (this.currentOptions.debugMode) {
        }
    }
    
    /**
     * Get target layer with chart ID index
     */
    private getTargetLayer(): any | null {
        const chartContent = d3.select(`.viewport-content-${this.chartId}`);
        
        if (chartContent.empty()) {
            devWarn(`❌ TabChartRenderer[${this.chartId}]: Viewport .viewport-content-${this.chartId} NOT FOUND - this will cause rendering in wrong chart!`);
        } else {
        }
        
        if (chartContent.empty()) {
            if (this.currentOptions.debugMode) {
                devWarn(`⚠️ TabChartRenderer[${this.chartId}]: Viewport not found, initializing...`);
            }
            this.initializeDOMStructure();
            
            const retryChartContent = d3.select(`.viewport-content-${this.chartId}`);
            if (retryChartContent.empty()) {
                devWarn(`⚠️ TabChartRenderer[${this.chartId}]: Viewport still not found after initialization`);
                return null;
            }
            return this.getTargetLayerFromContent(retryChartContent);
        }
        
        return this.getTargetLayerFromContent(chartContent);
    }
    
    /**
     * Ensure lines overlay exists with chart ID index
     */
    private ensureLinesOverlay(): any {
        const chartContent = d3.select(`.viewport-content-${this.chartId}`);
        if (chartContent.empty()) {
            devWarn(`⚠️ TabChartRenderer[${this.chartId}]: Viewport not found for lines-overlay`);
            return null;
        }
        
        let linesOverlay = chartContent.select('.lines-overlay') as any;
        if (linesOverlay.empty()) {
            linesOverlay = chartContent.append('g').attr('class', 'lines-overlay') as any;
            if (this.currentOptions.debugMode) {
            }
        }
        
        return linesOverlay;
    }
    
    /**
     * Get target layer from chart content with chart ID index
     */
    private getTargetLayerFromContent(chartContent: any): any | null {
        // Используем стандартное имя класса без chart ID
        const linesOverlay = chartContent.select('.lines-overlay') as any;
        
        if (linesOverlay.empty()) {
            devWarn(`⚠️ TabChartRenderer[${this.chartId}]: Lines overlay not found in viewport`);
            return null;
        }
        
        return linesOverlay;
    }
    
    /**
     * Subscribe to event and track for cleanup
     */
    private trackSubscription(eventType: string, callback: (event: any) => void, options?: any): void {
        this.eventSubscriptions.push(this.eventStream.subscribe(eventType, callback, options));
    }

    /**
     * Setup event subscriptions with chart ID filtering
     */
    private setupEventSubscriptions(): void {
        // DOM ready with chart ID filtering
        this.trackSubscription(`dom.ready.${this.chartId}`, (event: any) => {
            if (this.currentOptions.debugMode) {
            }
            this.initializeDOMStructure();
        });

        // Logo visibility control
        this.trackSubscription('settings.logo.changed', (event: any) => {
            const showLogo = event.showLogo;
            this.updateLogoVisibility(showLogo);
        });

        // Center image change control
        this.trackSubscription('settings.centerImage.changed', () => {
            // Re-render logo with new image (if visible)
            if (window.globalShowLogo) {
                this.updateLogoVisibility(true);
            }
        });
        
        // No deprecated event handlers - only TabChartEventManager should emit events
        
        // Container ready events with chart ID filtering
        this.trackSubscription('containers.ready', (event: any) => {
            if (event.chartId === this.chartId || !event.chartId) {
                if (event.metadata?.containerBounds) {
                    const boundsMap = new Map<string, ContainerBounds>();
                    
                    for (const [id, bounds] of Object.entries(event.metadata.containerBounds)) {
                        boundsMap.set(id, bounds as ContainerBounds);
                    }
                    
                    this.updateContainers(boundsMap);
                }
            }
        });
        
        // Zoom changes with chart ID filtering
        this.trackSubscription(`ZOOM_CHANGED_${this.chartId}`, async (event: any) => {
            const mode = this.getDisplayModeFromZoom(event.zoomLevel || 1.0);
            await this.switchDisplayMode(mode);
        });

        // Muhurta visibility control - MIGRATED TO UNIVERSAL RENDERER (uses same pattern as D7-D60)
        // MIGRATED: Muhurta now uses universal divisional handler (same as D7-D60)
        this.trackSubscription('muhurta.chakra.toggle', (event: any) => {
            if (isEventForChart(event, this.chartId)) {
                const showChakra = extractBooleanFromEvent(event, 'showMUHURTAChakra');
                const startPoint = event.data?.startPoint || 'sunrise';
                const customSettings = event.data?.customSettings;
                this.currentOptions.showMuhurtaSectors = showChakra;

                if (showChakra && this.currentChartData) {
                    // Use universal method instead of legacy prepareUniversalMuhurtaData
                    chakraDataService.prepareUniversalDivisionalData(
                        this.chartId,
                        'muhurta',
                        startPoint as any,
                        undefined, // rulers
                        customSettings
                    )
                        .then(data => {
                            this.eventStream.emit({
                                id: `render-muhurta-${this.chartId}-${Date.now()}`,
                                type: 'chakra.universal.render',
                                source: 'TabChartRenderer',
                                timestamp: Date.now(),
                                chartId: this.chartId,
                                targetSelector: `#chart-svg-${this.chartId}`,
                                data,
                                options: { debugMode: this.currentOptions.debugMode }
                            });
                            this.renderStatus.muhurta.isRendered = true;
                            this.renderStatus.muhurta.isVisible = true;
                        })
                        .catch(error => {
                            devError(`❌ TabChartRenderer[${this.chartId}]: Muhurta data preparation failed:`, error);
                        });
                } else if (!showChakra) {
                    this.eventStream.emit({
                        id: `hide-muhurta-${this.chartId}-${Date.now()}`,
                        type: 'chakra.universal.hide',
                        source: 'TabChartRenderer',
                        timestamp: Date.now(),
                        chartId: this.chartId,
                        chakraType: 'muhurta'
                    });
                    this.renderStatus.muhurta.isVisible = false;
                }
            }
        });

        // Muhurta start point change
        // MIGRATED: Muhurta now uses universal divisional handler (same as D7-D60)
        this.trackSubscription('muhurta.chakra.startpoint.change', (event: any) => {
            if (isEventForChart(event, this.chartId)) {
                const startPoint = event.data?.startPoint || 'sunrise';
                const customSettings = event.data?.customSettings;

                if (this.currentChartData) {
                    // Use universal method instead of legacy prepareUniversalMuhurtaData
                    chakraDataService.prepareUniversalDivisionalData(
                        this.chartId,
                        'muhurta',
                        startPoint as any,
                        undefined, // rulers
                        customSettings
                    )
                        .then(data => {
                            this.eventStream.emit({
                                id: `render-muhurta-${this.chartId}-${Date.now()}`,
                                type: 'chakra.universal.render',
                                source: 'TabChartRenderer',
                                timestamp: Date.now(),
                                chartId: this.chartId,
                                targetSelector: `#chart-svg-${this.chartId}`,
                                data,
                                options: { debugMode: this.currentOptions.debugMode }
                            });
                        })
                        .catch(error => {
                            devError(`❌ TabChartRenderer[${this.chartId}]: Muhurta re-render failed:`, error);
                        });
                }
            }
        });

        // Ashtamsha visibility control - MIGRATED TO UNIVERSAL RENDERER
        // Uses UniversalChakraRenderer via event system instead of legacy AshtamshaChakraDivisionsRenderer
        this.trackSubscription('ashtamsha.sectors.toggle', (event: any) => {
            if (isEventForChart(event, this.chartId)) {
                const showAshtamshaSectors = extractBooleanFromEvent(event, 'showAshtamshaSectors');
                this.currentOptions.showAshtamshaSectors = showAshtamshaSectors;

                if (showAshtamshaSectors) {
                    // Render via UniversalChakraRenderer
                    if (!this.renderStatus.ashtamsha.isRendered) {
                        // Prepare data and emit render event - MIGRATED to universal divisional method
                        chakraDataService.prepareUniversalDivisionalData(this.chartId, 'd8', 'sunrise')
                            .then((data: any) => {
                                this.eventStream.emit({
                                    id: `render-ashtamsha-${this.chartId}-${Date.now()}`,
                                    type: 'chakra.universal.render',
                                    source: 'TabChartRenderer',
                                    timestamp: Date.now(),
                                    chartId: this.chartId,
                                    targetSelector: `#chart-svg-${this.chartId}`,
                                    data,
                                    options: { debugMode: this.currentOptions.debugMode }
                                });
                                this.renderStatus.ashtamsha.isRendered = true;
                                this.renderStatus.ashtamsha.isVisible = true;
                                this.renderStatus.ashtamsha.divisionCount = 8;
                            })
                            .catch((error: any) => {
                                devError(`❌ TabChartRenderer[${this.chartId}]: Ashtamsha data preparation failed:`, error);
                            });
                    } else {
                        // Just show existing render
                        this.eventStream.emit({
                            id: `show-ashtamsha-${this.chartId}-${Date.now()}`,
                            type: 'chakra.universal.show',
                            source: 'TabChartRenderer',
                            timestamp: Date.now(),
                            chartId: this.chartId,
                            chakraType: 'ashtamsha'
                        });
                        this.renderStatus.ashtamsha.isVisible = true;
                    }
                } else {
                    // Hide chakra
                    this.eventStream.emit({
                        id: `hide-ashtamsha-${this.chartId}-${Date.now()}`,
                        type: 'chakra.universal.hide',
                        source: 'TabChartRenderer',
                        timestamp: Date.now(),
                        chartId: this.chartId,
                        chakraType: 'ashtamsha'
                    });
                    this.renderStatus.ashtamsha.isVisible = false;
                }
            }
        });

        // MIGRATED: Nakshatra now uses universal divisional handler (same as D7-D60)
        this.trackSubscription('nakshatra.chakra.toggle', (event: any) => {
            if (isEventForChart(event, this.chartId)) {
                const showChakra = extractBooleanFromEvent(event, 'showNAKSHATRAChakra');
                const startPoint = event.data?.startPoint || 'default'; // Nakshatra uses 'default' (0°)
                const customSettings = event.data?.customSettings;
                this.currentOptions.showNakshatraSectors = showChakra;


                if (showChakra) {
                    // Only render if chart data is ready
                    if (this.currentChartData) {
                        // Use universal method (same pattern as Muhurta/D7/D8)
                        chakraDataService.prepareUniversalDivisionalData(
                            this.chartId,
                            'nakshatra',
                            startPoint as any,
                            undefined, // no rulers for nakshatra (names are in sectors)
                            customSettings
                        )
                            .then((data: any) => {
                                this.eventStream.emit({
                                    id: `render-nakshatra-${this.chartId}-${Date.now()}`,
                                    type: 'chakra.universal.render',
                                    source: 'TabChartRenderer',
                                    timestamp: Date.now(),
                                    chartId: this.chartId,
                                    targetSelector: `#chart-svg-${this.chartId}`,
                                    data,
                                    options: {}
                                });
                                this.renderStatus.nakshatra.isVisible = true;
                            })
                            .catch((error: any) => {
                                devError('❌ Failed to prepare nakshatra data:', error);
                            });
                    }
                    // If currentChartData is null, just update options - restoreDivisionalChakrasState will handle it later
                } else {
                    // Hide chakra (only if chart is ready)
                    if (this.currentChartData) {
                        devLog(`🚫 TabChartRenderer[${this.chartId}]: Emitting chakra.universal.hide for nakshatra`);
                        this.eventStream.emit({
                            id: `hide-nakshatra-${this.chartId}-${Date.now()}`,
                            type: 'chakra.universal.hide',
                            source: 'TabChartRenderer',
                            timestamp: Date.now(),
                            chartId: this.chartId,
                            chakraType: 'nakshatra'
                        });
                    }
                    this.renderStatus.nakshatra.isVisible = false;
                }
            }
        });

        // Nakshatra Pada visibility control (depends on nakshatra)
        // NOTE: Pada renderer now handles its own event subscription
        // This legacy subscription is kept ONLY for status tracking

        this.trackSubscription('nakshatra.pada.toggle', (event: any) => {
            if (isEventForChart(event, this.chartId)) {
                const showPada = extractBooleanFromEvent(event, 'showPada');
                // Only track status - renderer handles actual show/hide
                this.currentOptions.showNakshatraPada = showPada;

                // Update status tracking
                if (showPada) {
                    this.renderStatus.nakshatra.isPadaRendered = true;
                    this.renderStatus.nakshatra.isPadaVisible = true;
                } else {
                    this.renderStatus.nakshatra.isPadaVisible = false;
                }
            }
        });

        // Nakshatra start point change
        this.trackSubscription('nakshatra.chakra.startpoint.change', (event: any) => {
            if (isEventForChart(event, this.chartId)) {
                const startPoint = event.data?.startPoint || 'default';
                const customSettings = event.data?.customSettings;

                if (this.currentChartData) {
                    // Re-render nakshatra with new start point (same pattern as Muhurta)
                    chakraDataService.prepareUniversalDivisionalData(
                        this.chartId,
                        'nakshatra',
                        startPoint as any,
                        undefined, // no rulers for nakshatra
                        customSettings
                    )
                        .then((data: any) => {
                            this.eventStream.emit({
                                id: `render-nakshatra-${this.chartId}-${Date.now()}`,
                                type: 'chakra.universal.render',
                                source: 'TabChartRenderer',
                                timestamp: Date.now(),
                                chartId: this.chartId,
                                targetSelector: `#chart-svg-${this.chartId}`,
                                data,
                                options: {}
                            });
                        })
                        .catch((error: any) => {
                            devError(`❌ TabChartRenderer[${this.chartId}]: Nakshatra re-render failed:`, error);
                        });
                }
            }
        });

        // Setup universal divisional chakras (D7, D8, D12, D20, D24, D30, D40, D60)
        this.setupUniversalDivisionalChakras();

        // House names visibility toggle (global setting from SettingsPanel)
        this.trackSubscription('house.names.toggle', (event: any) => {
            // Global event - applies to all charts
            const showNames = extractBooleanFromEvent(event, 'showHouseNames');
            if (showNames) {
                this.renderHouseNames();
            } else {
                this.hideHouseNames();
            }
            this.renderStatus.houseNames.isVisible = showNames;
        });

        // View switch events (chart.view.switch, hora.clock.design.changed) handled by ChartViewManager

        // Planet display mode change (global setting from SettingsPanel)
        this.trackSubscription('planet.displayMode.change', (event: any) => {
            // Global event - applies to all charts
            const mode = event.data?.mode as 'symbols' | 'short' | 'full';
            if (mode) {
                this.updatePlanetDisplayMode(mode);
            }
        });

        // Rashi display mode change (global setting from SettingsPanel)
        this.trackSubscription('settings.rashiDisplayMode.changed', (event: any) => {
            // Global event - applies to all charts
            // Re-render house names if visible
            if (this.renderStatus.houseNames.isVisible) {
                this.renderHouseNames();
            }
        });

        // Restore house names from localStorage when DOM is ready
        this.trackSubscription('chart.dom.ready', (event: any) => {
            if (isEventForChart(event, this.chartId)) {
                this.restoreHouseNamesFromStorage();
            }
        });
    }

    /**
     * Restore house names visibility from global localStorage setting
     */
    private restoreHouseNamesFromStorage(): void {
        try {
            // Restore display mode from global service first
            const savedMode = DisplayModeService.getDisplayMode();
            // Map 'full' to 'short' for ChartRenderOptions (only 'symbols' | 'short' supported)
            this.currentOptions.displayMode = savedMode === 'symbols' ? 'symbols' : 'short';

            // Read global setting (shared across all charts), force true for guest mode
            const isGuestMode = typeof window !== 'undefined' && window.sharedViewMode === 'guest';
            const showRasiNames = getLocalStorageItem<string | null>('hora-showRasiNames', null);
            if (isGuestMode || showRasiNames === 'true') {
                this.renderHouseNames();
                this.renderStatus.houseNames.isVisible = true;
            }
        } catch (e) {
            // localStorage not available
        }
    }

    /**
     * Setup universal divisional chakra event subscriptions
     * MIGRATED TO UNIVERSAL RENDERER - uses UniversalChakraRenderer via event system
     */
    private setupUniversalDivisionalChakras(): void {
        const divisionalTypes: DivisionalChakraType[] = ['d7', 'd8', 'd12', 'd20', 'd24', 'd30', 'd40', 'd60'];

        divisionalTypes.forEach(chakraType => {
            // Toggle visibility event
            this.trackSubscription(`${chakraType}.chakra.toggle`, (event: any) => {
                if (isEventForChart(event, this.chartId)) {
                    const showChakra = extractBooleanFromEvent(event, `show${chakraType.toUpperCase()}Chakra`);
                    const startPoint = event.data?.startPoint || 'sunrise';
                    const customSettings = event.data?.customSettings;
                    const optionKey = `show${chakraType.toUpperCase()}Sectors` as keyof ChartRenderOptions;
                    (this.currentOptions as any)[optionKey] = showChakra;

                    if (showChakra && this.currentChartData) {
                        // Render via UniversalChakraRenderer
                        chakraDataService.prepareUniversalDivisionalData(
                            this.chartId,
                            chakraType,
                            startPoint as any,
                            undefined, // rulers
                            customSettings
                        )
                            .then(data => {
                                this.eventStream.emit({
                                    id: `render-${chakraType}-${this.chartId}-${Date.now()}`,
                                    type: 'chakra.universal.render',
                                    source: 'TabChartRenderer',
                                    timestamp: Date.now(),
                                    chartId: this.chartId,
                                    targetSelector: `#chart-svg-${this.chartId}`,
                                    data,
                                    options: { debugMode: this.currentOptions.debugMode }
                                });
                            })
                            .catch(error => {
                                devError(`❌ TabChartRenderer[${this.chartId}]: ${chakraType} data preparation failed:`, error);
                            });
                    } else if (!showChakra) {
                        // Hide chakra
                        this.eventStream.emit({
                            id: `hide-${chakraType}-${this.chartId}-${Date.now()}`,
                            type: 'chakra.universal.hide',
                            source: 'TabChartRenderer',
                            timestamp: Date.now(),
                            chartId: this.chartId,
                            chakraType
                        });
                    }
                }
            });

            // Start point change event
            this.trackSubscription(`${chakraType}.chakra.startpoint.change`, (event: any) => {
                if (isEventForChart(event, this.chartId)) {
                    const startPoint = event.data?.startPoint || 'sunrise';
                    const customSettings = event.data?.customSettings;

                    if (this.currentChartData) {
                        // Re-render with new start point
                        chakraDataService.prepareUniversalDivisionalData(
                            this.chartId,
                            chakraType,
                            startPoint as any,
                            undefined, // rulers
                            customSettings
                        )
                            .then(data => {
                                this.eventStream.emit({
                                    id: `render-${chakraType}-${this.chartId}-${Date.now()}`,
                                    type: 'chakra.universal.render',
                                    source: 'TabChartRenderer',
                                    timestamp: Date.now(),
                                    chartId: this.chartId,
                                    targetSelector: `#chart-svg-${this.chartId}`,
                                    data,
                                    options: { debugMode: this.currentOptions.debugMode }
                                });
                            })
                            .catch(error => {
                                devError(`❌ TabChartRenderer[${this.chartId}]: ${chakraType} re-render failed:`, error);
                            });
                    }
                }
            });

            // Label update event (optimized - only updates label text, no full re-render)
            this.trackSubscription(`${chakraType}.chakra.label.update`, (event: any) => {
                if (isEventForChart(event, this.chartId)) {
                    const newLabel = event.data?.label;
                    if (newLabel) {
                        this.eventStream.emit({
                            id: `label-update-${chakraType}-${this.chartId}-${Date.now()}`,
                            type: 'chakra.universal.label.update',
                            source: 'TabChartRenderer',
                            timestamp: Date.now(),
                            chartId: this.chartId,
                            chakraType,
                            label: newLabel
                        });
                    }
                }
            });
        });

    }

    /**
     * Determine display mode from zoom level
     */
    private getDisplayModeFromZoom(zoomLevel: number): 'symbols' | 'short' {
        if (zoomLevel <= 1.0) return 'symbols';
        return 'short';
    }
    
    /**
     * Cleanup resources
     */
    cleanup(): void {
        // Unsubscribe from all event subscriptions
        this.eventSubscriptions.forEach(sub => sub.unsubscribe());
        this.eventSubscriptions = [];

        this.containers.clear();
        this.isInitialized = false;
        this.viewManager.destroy();

        // Muhurta/Ashtamsha/Nakshatra/Pada: cleanup handled by UniversalChakraRendererManager
    }
    
    /**
     * Recreate background elements with new colors
     */
    recreateBackgroundElements(): void {
        const chartContent = d3.select(`.viewport-content-${this.chartId}`);
        if (!chartContent.empty()) {
            this.createBackgroundElements(chartContent);
        }
    }

    /**
     * Render house names (Rashi names) in each house
     * - symbols mode: top-left corner
     * - short/full mode: bottom-left corner
     */
    private renderHouseNames(): void {
        const houseNamesGroup = d3.select(`#house-names-container-${this.chartId}`);
        if (houseNamesGroup.empty()) {
            devWarn(`⚠️ TabChartRenderer[${this.chartId}]: House names container not found`);
            return;
        }

        // Clear existing names
        houseNamesGroup.selectAll('*').remove();

        // Get styles from preset
        const preset = chartColorPresetManager.getCurrentPreset();
        const styles = preset.houseNames;

        // Get rashi display mode from global setting (force 'symbols' for guest mode)
        const isGuestMode = typeof window !== 'undefined' && window.sharedViewMode === 'guest';
        const rashiMode = isGuestMode ? 'symbols' : ((typeof window !== 'undefined' && (window as any).globalRashiDisplayMode) || 'symbols');
        const currentMode = rashiMode as 'symbols' | 'short' | 'full' | 'devanagari';
        const useSymbols = rashiMode === 'symbols' && styles.symbolsMode;

        // Get mode-specific styles
        const fontSize = useSymbols ? styles.symbolsMode!.fontSize : styles.fontSize;
        const fontFamily = useSymbols ? styles.symbolsMode!.fontFamily : styles.fontFamily;
        const fontWeight = useSymbols ? styles.symbolsMode!.fontWeight : styles.fontWeight;
        const dy = useSymbols ? (styles.symbolsMode!.dy || 0) : 0;

        // Vertical alignment in short/full mode
        // Bottom: Meena (0), Mesha (1), Vrishabha (2), Mithuna (3)
        const bottomAlignedContainers = [0, 1, 2, 3];
        // Vertical center: Karka (4), Simha (5), Makara (10), Kumbha (11)
        const verticalCenterContainers = [4, 5, 10, 11];
        // Top: Kanya (6), Tula (7), Vrishchika (8), Dhanu (9)

        // Horizontal alignment in short/full mode
        // Right: Meena (0), Kumbha (11), Makara (10), Dhanu (9)
        const rightAlignedContainers = [0, 9, 10, 11];
        // Horizontal center: Mesha (1), Vrishabha (2), Tula (7), Vrishchika (8)
        const horizontalCenterContainers = [1, 2, 7, 8];
        // Left: Mithuna (3), Karka (4), Simha (5), Kanya (6)

        // Render name for each house (containerIndex 0-11)
        for (let containerIndex = 0; containerIndex < 12; containerIndex++) {
            const position = this.calculateHousePosition(containerIndex);
            const name = getRashiDisplayName(containerIndex, currentMode);

            // Position based on container (same alignment for all modes)
            const useBottomAlignment = bottomAlignedContainers.includes(containerIndex);
            const useVerticalCenter = verticalCenterContainers.includes(containerIndex);
            const useRightAlignment = rightAlignedContainers.includes(containerIndex);
            const useHorizontalCenter = horizontalCenterContainers.includes(containerIndex);

            let textX: number;
            let textAnchor: string;
            if (useHorizontalCenter) {
                textX = position.x + position.width / 2;  // center alignment
                textAnchor = 'middle';
            } else if (useRightAlignment) {
                textX = position.x + position.width - 4;  // right alignment
                textAnchor = 'end';
            } else {
                textX = position.x + 4;  // left alignment
                textAnchor = 'start';
            }

            let textY: number;
            if (useBottomAlignment) {
                textY = position.y + position.height - 4;  // bottom alignment
            } else if (useVerticalCenter) {
                textY = position.y + position.height / 2 + fontSize / 3;  // vertical center
            } else {
                textY = position.y + fontSize + 2;  // top alignment
            }

            houseNamesGroup.append('text')
                .attr('class', `house-name house-name-${containerIndex}`)
                .attr('x', textX)
                .attr('y', textY)
                .attr('dy', dy)
                .attr('text-anchor', textAnchor)
                .attr('font-size', fontSize)
                .attr('font-family', fontFamily)
                .attr('font-weight', fontWeight)
                .attr('fill', styles.fill)
                .attr('opacity', styles.opacity)
                .text(name);
        }

        if (this.currentOptions.debugMode) {
            devLog(`✅ TabChartRenderer[${this.chartId}]: Rendered 12 house names (rashiMode: ${rashiMode})`);
        }
    }

    /**
     * Hide house names
     */
    private hideHouseNames(): void {
        const houseNamesGroup = d3.select(`#house-names-container-${this.chartId}`);
        if (!houseNamesGroup.empty()) {
            houseNamesGroup.selectAll('*').remove();
        }

        if (this.currentOptions.debugMode) {
            devLog(`🚫 TabChartRenderer[${this.chartId}]: House names hidden`);
        }
    }

    /**
     * Update planet display mode (symbols/short/full) - instant update via D3
     */
    private updatePlanetDisplayMode(mode: 'symbols' | 'short' | 'full'): void {
        // Update current options
        this.currentOptions.displayMode = mode === 'full' ? 'short' : mode;

        // Get mode-specific text settings from preset
        const modeTextConfig = chartColorPresetManager.getCurrentPreset().planetSprites.modes[mode];

        // Find all planet text elements for this chart and update them
        const spriteGroup = d3.select(`#planet-sprites-${this.chartId}`);
        if (spriteGroup.empty()) {
            return;
        }

        spriteGroup.selectAll('.planet-text').each(function() {
            const textElement = d3.select(this);
            const container = d3.select((this as Element).parentNode as Element);
            const planetName = container.attr('data-planet');

            if (planetName) {
                // Check if planet is retrograde by looking at current text (has parentheses)
                const currentText = textElement.text();
                const isRetrograde = currentText.startsWith('(') && currentText.endsWith(')');

                // Use unified function for planet display name
                const newDisplayName = getFormattedPlanetDisplayName(planetName, mode, isRetrograde);

                // Get current fill color for stroke
                const currentFill = textElement.attr('fill') || '#374151';

                // Lg (Lagna) doesn't need vertical offset - it's already centered
                const dyOffset = (planetName === 'Lg') ? 0 : (modeTextConfig.dy || 0);

                // Update text content and styling
                textElement
                    .text(newDisplayName)
                    .attr('dy', dyOffset)
                    .attr('font-size', modeTextConfig.fontSize)
                    .attr('font-weight', modeTextConfig.fontWeight)
                    .attr('font-family', modeTextConfig.fontFamily || 'Arial, sans-serif')
                    .attr('stroke', modeTextConfig.textStroke ? currentFill : 'none')
                    .attr('stroke-width', modeTextConfig.textStroke || 0);
            }
        });
    }

    /**
     * Check if this chart is currently visible/active
     */
    private isChartVisible(): boolean {
        try {
            const multipleChartsContext = window.multipleChartsContext;
            if (multipleChartsContext && multipleChartsContext.activeChartId) {
                return multipleChartsContext.activeChartId === this.chartId;
            }

            const tabElement = document.querySelector(`[data-chart-id="${this.chartId}"]`);
            const isVisible = tabElement && !tabElement.classList.contains('hidden') &&
                             getComputedStyle(tabElement).display !== 'none';
            return !!isVisible;
        } catch (error) {
            return true;
        }
    }

    /**
     * Render planets for this chart if they are pending and chart becomes visible
     */
    public renderPendingPlanets(): void {
        if (this.pendingPlanetRendering && this.pendingChartData && this.isChartVisible()) {
            // Clear pending state first to avoid infinite loops
            this.pendingPlanetRendering = false;
            const chartDataToRender = this.pendingChartData;
            this.pendingChartData = null;

            // Trigger planet rendering by calling renderChart again, but this time chart will be visible
            this.renderChart(chartDataToRender, this.currentOptions);
        }
    }

    /**
     * НОВАЯ СИСТЕМА: Установка активности таба и проверка готовности для lazy loading
     */
    public setTabActive(isActive: boolean): void {
        const wasActive = this.renderStatus.tab.isActive;
        this.renderStatus.tab.isActive = isActive;

        if (isActive && !wasActive) {
            this.checkChakraInitializationReadiness();
        }
    }

    /**
     * НОВАЯ СИСТЕМА: Проверка готовности для инициализации чакр (только для активного таба)
     */
    private checkChakraInitializationReadiness(): void {
        if (!this.renderStatus.tab.isActive) {
            return;
        }

        if (!this.renderStatus.tab.isDOMReady || !this.renderStatus.tab.isPlanetsReady) {
            return;
        }

        this.renderStatus.tab.readyTime = performance.now();

        // Muhurta: migrated to UniversalChakraRenderer - initialization handled by event system
        // Ashtamsha: migrated to UniversalChakraRenderer - initialization handled by event system
        // Nakshatra: migrated to UniversalChakraRenderer - initialization handled by event system
    }

    // ─────────────────────────────────────────────────────────────────────
    // View switching — delegated to ChartViewManager
    // ─────────────────────────────────────────────────────────────────────

    /** Expose active view for external consumers */
    getActiveView(): ChartViewMode {
        return this.viewManager.getActiveView();
    }

    /**
     * Called when returning to 'chakra' view from a non-default view.
     * 1) Render overlays that should be visible but were never rendered (e.g. chart loaded in horaClock mode)
     * 2) Hide overlays that are rendered but should be off
     */
    private onChakraViewRestored(): void {
        this.ensureOverlaysRendered();
        this.restoreChakraLayerVisibility();
    }

    /**
     * Render chakra overlays that should be enabled (per settings) but are not yet rendered.
     * Covers the case when chart was loaded in a non-chakra view and overlays were never triggered.
     */
    private ensureOverlaysRendered(): void {
        if (!this.currentChartData) return;

        let settings: any = null;
        try {
            const ctx = window.multipleChartsContext;
            if (ctx && this.chartId) {
                settings = ctx.getChart?.(this.chartId)?.settings;
            }
            if (!settings && this.chartId) {
                settings = getLocalStorageItem<any | null>(`chart-settings-${this.chartId}`, null);
            }
        } catch { /* ignore */ }
        if (!settings) return;

        // Muhurta
        if (settings.showMUHURTAChakra === true && !this.renderStatus.muhurta.isRendered) {
            const startPoint = settings.muhurtaStartPoint || 'sunrise';
            const customSettings = settings.muhurtaCustomSettings;
            this.eventStream.emit({
                id: `muhurta-ensure-${this.chartId}-${Date.now()}`,
                type: 'muhurta.chakra.toggle',
                source: 'TabChartRenderer-ensure',
                timestamp: Date.now(),
                data: {
                    chartId: this.chartId,
                    showMUHURTAChakra: true,
                    startPoint,
                    customSettings: startPoint === 'custom' ? customSettings : undefined
                }
            });
        }

        // Nakshatra
        if ((settings.showNakshatraChakra === true || settings.showNAKSHATRAChakra === true)
            && !this.renderStatus.nakshatra.isVisible) {
            const startPoint = settings.nakshatraStartPoint || 'default';
            const customSettings = settings.nakshatraCustomSettings;
            this.eventStream.emit({
                id: `nakshatra-ensure-${this.chartId}-${Date.now()}`,
                type: 'nakshatra.chakra.toggle',
                source: 'TabChartRenderer-ensure',
                timestamp: Date.now(),
                data: {
                    chartId: this.chartId,
                    showNAKSHATRAChakra: true,
                    startPoint,
                    customSettings: startPoint === 'custom' ? customSettings : undefined
                }
            });
        }

        // Divisional chakras (D7–D60)
        const divisionalTypes: DivisionalChakraType[] = ['d7', 'd8', 'd12', 'd20', 'd24', 'd30', 'd40', 'd60'];
        for (const chakraType of divisionalTypes) {
            const settingKey = `show${chakraType.toUpperCase()}Chakra`;
            const optionKey = `show${chakraType.toUpperCase()}Sectors`;
            if ((settings as any)[settingKey] === true && !(this.currentOptions as any)[optionKey]) {
                const startPoint = (settings as any)[`${chakraType}StartPoint`] || 'sunrise';
                const customSettings = (settings as any)[`${chakraType}CustomSettings`];
                this.eventStream.emit({
                    id: `${chakraType}-ensure-${this.chartId}-${Date.now()}`,
                    type: `${chakraType}.chakra.toggle`,
                    source: 'TabChartRenderer-ensure',
                    timestamp: Date.now(),
                    data: {
                        chartId: this.chartId,
                        [`show${chakraType.toUpperCase()}Chakra`]: true,
                        startPoint,
                        customSettings: startPoint === 'custom' ? customSettings : undefined
                    }
                });
            }
        }
    }

    /**
     * Re-hide chakra overlay layers that are rendered but toggled off.
     */
    private restoreChakraLayerVisibility(): void {
        const chakrasToCheck: Array<{ visible: boolean; type: string }> = [
            { visible: this.renderStatus.muhurta.isVisible, type: 'muhurta' },
            { visible: this.renderStatus.nakshatra.isVisible, type: 'nakshatra' },
            { visible: this.renderStatus.ashtamsha.isVisible, type: 'ashtamsha' },
        ];
        const divisionalTypes = ['d7', 'd8', 'd12', 'd20', 'd24', 'd30', 'd40', 'd60'];
        for (const dt of divisionalTypes) {
            const optionKey = `show${dt.toUpperCase()}Sectors`;
            chakrasToCheck.push({
                visible: !!(this.currentOptions as any)[optionKey],
                type: dt
            });
        }
        for (const chakra of chakrasToCheck) {
            if (!chakra.visible) {
                this.eventStream.emit({
                    id: `hide-${chakra.type}-post-view-${this.chartId}`,
                    type: 'chakra.universal.hide',
                    source: 'TabChartRenderer',
                    timestamp: Date.now(),
                    chartId: this.chartId,
                    chakraType: chakra.type
                });
            }
        }
    }

    // renderHoraClockView and renderRashiChakraView moved to view adapters (HoraClockView, RashiChakraView)

    /**
     * Get the chart viewport content D3 selection
     */
    private getChartContent(): d3.Selection<any, unknown, HTMLElement, any> | null {
        const sel = d3.select(`#viewport-content-${this.chartId}`);
        return sel.empty() ? null : sel;
    }

    /**
     * Restore divisional chakras state from saved settings
     * Called after renderChart when currentChartData is available
     * Fixes: chakra toggle state persists but chakra doesn't render after page reload
     */
    private restoreDivisionalChakrasState(): void {
        if (!this.currentChartData) return;

        try {
            // Try to get settings from context first, fallback to localStorage
            const multipleChartsContext = window.multipleChartsContext;
            let settings: any = null;

            if (multipleChartsContext && this.chartId) {
                const activeChart = multipleChartsContext.getChart?.(this.chartId);
                settings = activeChart?.settings;
            }

            // Fallback to localStorage if context not ready yet (race condition fix)
            if (!settings && this.chartId) {
                settings = getLocalStorageItem<any | null>(`chart-settings-${this.chartId}`, null);
            }

            if (!settings) return;

            // If saved view is non-chakra, switch view — overlays will be rendered on return to 'chakra'
            const savedView: ChartViewMode = settings.activeView || 'chakra';
            if (savedView !== 'chakra') {
                this.viewManager.restoreFromSettings(savedView);
                return;
            }

            // Restore Muhurta chakra (hook saves as showMUHURTAChakra)
            if (settings.showMUHURTAChakra === true) {
                const startPoint = settings.muhurtaStartPoint || 'sunrise';
                const customSettings = settings.muhurtaCustomSettings;
                this.eventStream.emit({
                    id: `muhurta-restore-${this.chartId}-${Date.now()}`,
                    type: 'muhurta.chakra.toggle',
                    source: 'TabChartRenderer-restore',
                    timestamp: Date.now(),
                    data: {
                        chartId: this.chartId,
                        showMUHURTAChakra: true,
                        startPoint,
                        customSettings: startPoint === 'custom' ? customSettings : undefined
                    }
                });
            }

            // Restore Nakshatra chakra (context saves as showNakshatraChakra, hook saves as showNAKSHATRAChakra)
            if (settings.showNakshatraChakra === true || settings.showNAKSHATRAChakra === true) {
                // Nakshatra default is 'default' (0° Ashwini), not 'sunrise'
                const startPoint = settings.nakshatraStartPoint || 'default';
                const customSettings = settings.nakshatraCustomSettings;
                this.eventStream.emit({
                    id: `nakshatra-restore-${this.chartId}-${Date.now()}`,
                    type: 'nakshatra.chakra.toggle',
                    source: 'TabChartRenderer-restore',
                    timestamp: Date.now(),
                    data: {
                        chartId: this.chartId,
                        showNAKSHATRAChakra: true,
                        startPoint,
                        customSettings: startPoint === 'custom' ? customSettings : undefined
                    }
                });
            }

            // Restore divisional chakras (D7, D8, D12, D20, D24, D30, D40, D60)
            const divisionalTypes: DivisionalChakraType[] = ['d7', 'd8', 'd12', 'd20', 'd24', 'd30', 'd40', 'd60'];

            divisionalTypes.forEach(chakraType => {
                const settingKey = `show${chakraType.toUpperCase()}Chakra`;
                const isEnabled = (settings as any)[settingKey];

                if (isEnabled === true) {
                    // Get start point and custom settings
                    const startPointKey = `${chakraType}StartPoint`;
                    const customSettingsKey = `${chakraType}CustomSettings`;
                    const startPoint = (settings as any)[startPointKey] || 'sunrise';
                    const customSettings = (settings as any)[customSettingsKey];

                    // Emit toggle event to trigger chakra render
                    this.eventStream.emit({
                        id: `${chakraType}-restore-${this.chartId}-${Date.now()}`,
                        type: `${chakraType}.chakra.toggle`,
                        source: `TabChartRenderer-restore`,
                        timestamp: Date.now(),
                        data: {
                            chartId: this.chartId,
                            [`show${chakraType.toUpperCase()}Chakra`]: true,
                            startPoint,
                            customSettings: startPoint === 'custom' ? customSettings : undefined
                        }
                    });
                }
            });

        } catch (error) {
            devWarn(`⚠️ TabChartRenderer[${this.chartId}]: Failed to restore divisional chakras:`, error);
        }
    }

    /* REMOVED: initializeMuhurtaRendererIfNeeded
     * Migration: Muhurta now uses UniversalChakraRenderer - no initialization needed
     */

    /* REMOVED: initializeAshtamshaRendererIfNeeded
     * Migration: Ashtamsha now uses UniversalChakraRenderer - no initialization needed
     */

    // REMOVED: Legacy initializeNakshatraRendererIfNeeded method
    // Nakshatra now handled by UniversalChakraRenderer via event system

    /**
     * НОВАЯ СИСТЕМА: Сброс статуса рендеров (при смене данных или перезагрузке)
     */
    public resetRenderStatus(type?: 'planets' | 'muhurta' | 'tab' | 'all'): void {
        const resetPlanets = () => {
            this.renderStatus.planets = {
                isRendered: false,
                isVisible: true,
                renderTime: 0,
                elementCount: 0
            };
        };

        const resetMuhurta = () => {
            this.renderStatus.muhurta = {
                isRendered: false,
                isVisible: false,
                renderTime: 0,
                divisionCount: 0
            };
        };

        switch (type) {
            case 'planets':
                resetPlanets();
                break;
            case 'muhurta':
                resetMuhurta();
                break;
            default:
                resetPlanets();
                resetMuhurta();
        }
    }

    /**
     * Get renderer statistics including render status
     */
    getStats() {
        return {
            chartId: this.chartId,
            isInitialized: this.isInitialized,
            containerCount: this.containers.size,
            currentMode: this.currentOptions.displayMode,
            // НОВАЯ СИСТЕМА: Статус всех рендеров
            renderStatus: {
                planets: { ...this.renderStatus.planets },
                muhurta: { ...this.renderStatus.muhurta }
            },
            enginesStatus: {
                preparation: !!this.engines.preparation,
                calculation: !!this.engines.calculation,
                rendering: !!this.engines.rendering
            }
        };
    }
}